import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWebSocket } from '../contexts/WebSocketContext';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import * as apiService from '../services/apiService';
import { DispenseTransaction } from '../types/DrinkTypes';
import { useAppSettings } from '../contexts/AppSettingsContext';
import BackButton from '../components/BackButton';

interface LocationState {
    transaction_id: string;
}

const PaymentScreen: React.FC = () => {
    const { settings, appSettingsReady } = useAppSettings();

    const navigate = useNavigate();
    const { onMessage, offMessage } = useWebSocket();
    const MySwal = withReactContent(Swal);

    const location = useLocation();
    const state = location.state as LocationState;
    const transactionId = state?.transaction_id;

    const paymentTimeout = settings.paymentTimeout * 1000;

    useEffect(() => {
        if (!transactionId) {
            console.error("No transaction ID provided. Redirecting to select-drink.");
            navigate('/select-drink');
            return;
        }

        const handlePaymentAccepted = () => {
            console.log("Payment accepted, navigating to dispensing screen.");
            navigate('/dispensing', { state: { transaction_id: transactionId } });
        };

        const handlePaymentFailed = () => {
            MySwal.fire({
                title: 'Payment Failed',
                text: 'Please try again. Redirecting to the previous screen...',
                icon: 'error',
                timer: 5000,
                showConfirmButton: false,
                willClose: () => {
                    navigate('/select-drink');
                }
            });
        };

        const disableWebSocket = settings.disableWebSocket;

        if (disableWebSocket) {
            const timeoutId = setTimeout(handlePaymentAccepted, 5000); // Wait for 5 seconds and then call handlePaymentAccepted
            return () => clearTimeout(timeoutId);
        } else {


            const timeoutId = setTimeout(async () => {
                try {
                    const currentTransaction: DispenseTransaction = {
                        transaction_id: transactionId || ''
                    };

                    await apiService.cancelCurrentDispensing(currentTransaction);

                    MySwal.fire({
                        title: 'Payment Timeout',
                        text: 'Payment was not completed in time. Redirecting to home...',
                        icon: 'info',
                        timer: 5000,
                        showConfirmButton: false,
                        willClose: () => {
                            navigate('/');
                        }
                    });
                } catch (error) {
                    console.error("Error cancelling transaction:", error);
                }
            }, paymentTimeout);

            onMessage('paymentAccepted', handlePaymentAccepted);
            onMessage('paymentFailed', handlePaymentFailed);

            return () => {
                clearTimeout(timeoutId);
                offMessage('paymentAccepted', handlePaymentAccepted);
                offMessage('paymentFailed', handlePaymentFailed);
            };
        }
    }, [onMessage, offMessage, navigate, MySwal, transactionId, settings, appSettingsReady]);

    if (!appSettingsReady) {
        return <div>Loading...</div>;
    }

    return (
        <div data-layer="PAYMENT PAGE" className="PaymentPage w-[1080px] h-[1920px] relative bg-white overflow-hidden">
            <BackButton onClick={() => navigate("/select-drink")} />
            <img data-layer="ChatGPT Image 23 juin 2025, 14_30_27 1" className="ChatgptImage23Juin20251430271 w-[1280px] h-[1920px] left-0 top-0 absolute" src="/assets/images/new_design/home/fond/image-fond-kissd-3.png" />
            <div data-layer="Rectangle 2" className="Rectangle2 w-[1080px] h-[1920px] left-0 top-0 absolute bg-teal-500/40" />
            <div data-layer="Rectangle 6" className="Rectangle6 w-[906px] h-[1775px] left-[87px] top-[239px] absolute bg-white/30 rounded-[62px] shadow-[6px_7px_27.700000762939453px_-11px_rgba(0,0,0,0.42)]" />
            <div data-layer="TAP YOUR CARD ON THE READER TO PAY" className="TapYourCardOnTheReadyPlayer w-[846px] h-[465px] left-[117px] top-[523px] absolute text-center justify-start text-white text-[90px] font-extrabold font-['Montserrat'] leading-[115px]">TAP YOUR <br/>CARD ON THE READER TO PAY</div>
            <img data-layer="terminal-paiement-2 1" className="TerminalPaiement21 w-[847px] h-[847px] left-[116px] top-[808px] absolute" src="/assets/images/new_design/payment/terminal-paiement-3.png" />
            <img data-layer="freepik__3d-floating-transparent-bubbles-with-green-and-blu__24199 1" className="Freepik3dFloatingTransparentBubblesWithGreenAndBlu241991 w-72 h-96 left-[-50px] top-[-65px] absolute mix-blend-screen" src="/assets/images/new_design/payment/bubbles-1.svg" />
            <img data-layer="freepik__3d-floating-transparent-bubbles-with-green-and-blu__24199 2" className="Freepik3dFloatingTransparentBubblesWithGreenAndBlu241992 w-[450px] h-[975px] left-[782px] top-[1200px] absolute origin-top-left rotate-[14.04deg] mix-blend-screen" src="/assets/images/new_design/payment/bubbles-2.svg" />
            <img data-layer="bulles-2-fond-ecran 1" className="Bulles2FondEcran1 w-48 h-[588px] left-[824px] top-[-50px] absolute origin-top-left rotate-[-16.71deg] mix-blend-soft-light" src="/assets/images/new_design/payment/bulles-ecran-1.svg" />
            <img data-layer="bulles-2-fond-ecran 2" className="Bulles2FondEcran2 w-48 h-[588px] left-[15px] top-[863px] absolute origin-top-left rotate-[8.99deg] mix-blend-soft-light" src="/assets/images/new_design/payment/bulles-ecran-2.svg" />
            </div>
    );
};

export default PaymentScreen;
